import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableRow from '@mui/material/TableRow';
import { Button, LeftIcon, RightIcon } from '@uy3/web-components';
import { maskCurrencyInCents, formatDateFromNumberString } from 'helpers';
import { TableBody, TableCell } from '@mui/material';
import { IDataprevEmployee } from 'services/zetra/types/generics';

interface ContractDetails {
    name: string;
    value: string | number;
}

interface ContractOptionsProps {
    headerData?: IDataprevEmployee;
    handleDrawerClose: () => void;
}

export const ContractOptions: React.FC<ContractOptionsProps> = ({
    headerData,
    handleDrawerClose
}) => {
    const [selectedContract, setSelectedContract] = useState<number | null>(null);

    const contracts =
        headerData?.emprestimosLegados?.map((emprestimo) => ({
            label: `Contrato: ${emprestimo?.contrato}`,
            details: [
                { name: 'Contrato', value: emprestimo?.contrato },
                { name: 'CBC', value: emprestimo?.codigoIF },
                { name: 'Tipo do Contrato', value: emprestimo?.tipoContrato.descricao },
                {
                    name: 'Valor do Empréstimo',
                    value: maskCurrencyInCents(emprestimo?.valorEmprestimo)
                },
                { name: 'Parcelas', value: emprestimo?.totalParcelas },
                { name: 'Valor da Parcela', value: maskCurrencyInCents(emprestimo?.valorParcela) },
                { name: 'Parcelas Pagas', value: emprestimo?.qtdParcelasPagas },
                {
                    name: 'Valor do Saldo Devedor',
                    value: maskCurrencyInCents(emprestimo?.valorSaldoDevedor)
                },
                { name: 'Valor da Taxa Mensal', value: `${emprestimo?.valorTaxaMensal}%` },
                { name: 'Valor CET Mensal', value: `${emprestimo?.valorCETMensal}%` },
                {
                    name: 'Data Início do Contrato',
                    value: formatDateFromNumberString(emprestimo?.dataInicioContrato)
                },
                {
                    name: 'Data Fim do Contrato',
                    value: formatDateFromNumberString(emprestimo?.dataFimContrato)
                }
            ]
        })) || [];

    const handleSelectContract = (index: number) => {
        setSelectedContract(index);
    };

    const buttons = [
        {
            label: 'Ok, Entendi',
            variant: 'contained' as 'contained',
            onClick: handleDrawerClose,
            startIcon: <RightIcon htmlColor="#fff" />,
            sx: {
                backgroundColor: '#D04D27',
                color: '#FFF',
                '&:hover': {
                    backgroundColor: '#A03A1F'
                }
            }
        },
        {
            label: 'Voltar',
            variant: 'outlined' as 'outlined',
            onClick: () => setSelectedContract(null),
            startIcon: <LeftIcon htmlColor="#D04D27" />
        }
    ];

    const renderButtons = buttons.map((button, idx) => (
        <Button key={idx} {...button}>
            {button.label}
        </Button>
    ));

    return (
        <Box sx={{ width: '100%' }}>
            {selectedContract === null ? (
                contracts.map((contract, index) => (
                    <Stack
                        key={index}
                        sx={{
                            height: 75,
                            justifyContent: 'space-between',
                            borderRadius: 2,
                            borderWidth: 1,
                            borderStyle: 'solid',
                            borderColor: '#BABFD0',
                            padding: '24px',
                            display: 'flex',
                            flexDirection: 'row',
                            cursor: 'pointer',
                            mb: 2,
                            transition: 'border-color 0.2s, box-shadow 0.2s',
                            '&:hover': {
                                borderColor: '#D04D27'
                            }
                        }}
                        onClick={() => handleSelectContract(index)}
                    >
                        <Typography
                            sx={{
                                fontFamily: 'Lato',
                                fontWeight: 600,
                                fontSize: 16,
                                lineHeight: '120%',
                                letterSpacing: '-0.2px',
                                color: '#000'
                            }}
                        >
                            {contract.label}
                        </Typography>
                        <RightIcon
                            style={{
                                height: 24,
                                width: 24
                            }}
                            htmlColor="#D04D27"
                        />
                    </Stack>
                ))
            ) : (
                <Box
                    sx={{
                        width: '100%',
                        pl: 3,
                        pr: 3,
                        borderRadius: 2,
                        border: '1px solid #BABFD0',
                        mb: 2
                    }}
                >
                    <Table>
                        <TableBody>
                            {contracts[selectedContract].details.map((detail, idx) => (
                                <TableRow
                                    key={idx}
                                    sx={{
                                        '&:last-child td, &:last-child th': {
                                            borderBottom: 'none'
                                        },
                                        '& .MuiTableCell-root': {
                                            paddingLeft: 0,
                                            paddingRight: 0
                                        }
                                    }}
                                >
                                    {['name', 'value'].map((key, cellIdx) => (
                                        <TableCell
                                            key={cellIdx}
                                            sx={{
                                                fontFamily: 'Lato',
                                                fontWeight: key === 'name' ? 600 : 400,
                                                fontSize: 16,
                                                color: key === 'name' ? '#000' : '#555',
                                                textAlign: key === 'value' ? 'right' : 'left'
                                            }}
                                        >
                                            {detail[key as keyof ContractDetails]}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}
            {selectedContract !== null && (
                <Stack
                    sx={{
                        mt: 3,
                        gap: 2
                    }}
                >
                    {renderButtons}
                </Stack>
            )}
        </Box>
    );
};
